import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Anchor, Box, Group, Text } from "@mantine/core";
import { notifyError, notifySuccess } from "../../utils/notify";
import moment from "moment";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { useTransaction } from "../../context/TransactionContext";
import {
  GetTransactionsResponse,
  Payment,
  TransactionResponse,
} from "../../types/transactions";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { formatDateTime, DEFAULT_TIMEZONE } from "../../utils/timeUtils";
import { formatPaymentMethod } from "../../utils/paymentMethodFormatters";
import { visibleRefunds } from "../../utils/transactionTotals";
import { transformTransactionResponseToGetTransactionsResponse } from "../../utils/transactionUtils";
import RefundPaymentModal from "../checkout/RefundPaymentModal";
import TransactionDetailModal from "../calendar/CalendarView/components/TransactionDetailModal/TransactionDetailModal";
import useReceiptPrint from "../common/useReceiptPrint";
import SummaryStrip from "./shared/SummaryStrip";
import SalesToolbar from "./shared/SalesToolbar";
import { SalesTable, SalesTableRow } from "./shared/SalesTable";
import SalesPagination from "./shared/SalesPagination";
import { SalesStatusKey } from "./shared/StatusCell";
import TransactionDetailDrawer from "./shared/TransactionDetailDrawer";
import VoidTransactionModal from "./shared/VoidTransactionModal";
import { PaymentMethodIcon, methodLabel } from "./shared/paymentMethodIcon";
import EmailReceiptModal from "./shared/EmailReceiptModal";
import { useSalesDrawerSlot } from "./shared/salesDrawerSlot";
import { matchesTagFilter, SalesFilters, toIso } from "./shared/filters";
import { AppointmentTag } from "../../types/appointmentTags";
import SalesDateRangePill from "./shared/SalesDateRangePill";
import { DateRangePreset } from "../../types/reports";
import {
  resolveReportsRange,
  reportsRangeReady,
} from "./shared/reportsDateRange";
import {
  clientName,
  isCashTender,
  listTenders,
} from "./shared/salesDerivations";
import styles from "./shared/sales.module.css";

// Transaction Id (120px, fits the header one line), Date (122px), Method
// (150px fixed — brand+last4 is short; longer labels ellipsis), Type (104px),
// Client (flex), Amount (108px), chevron (34px).
const COLUMN_TEMPLATE = "120px 122px 150px 104px 1fr 108px 34px";
const MIN_WIDTH = 900;

const STATUS_OPTIONS = [
  { value: "all", label: "All results" },
  { value: "captured", label: "Captured" },
  { value: "refunded", label: "Refunded" },
];
const DEFAULT_STATUS = "all";

/** One flattened tender (or refund) row. */
interface TenderRow {
  key: string;
  parent: GetTransactionsResponse;
  /** Underlying captured tender, when this row is one (enables the refund action). */
  payment?: Payment;
  /** Display label for the method (e.g. "Card on File"). */
  method: string;
  /** Raw backend method code, driving the tender icon. */
  rawMethod: string;
  /** Card brand for card tenders (e.g. "Visa"); undefined for cash/refunds. */
  cardBrand?: string;
  /** Last 4 of card for captured card tenders; undefined for cash/refunds. */
  last4?: string;
  /** Payment type (deposit/checkout/refund/…) for the Type column. */
  type: string;
  amount: number; // negative for refunds
  statusKey: Extract<SalesStatusKey, "captured" | "refunded">;
  time: string;
  isRefund: boolean;
  /** Real cash-bearing tender — loyalty/package pseudo-tenders are false and
   *  are shown as rows but excluded from the Captured/Net cash totals. */
  isCash: boolean;
}

interface PurchasesSubTabProps {
  refreshNonce?: number;
  /** Filter + search state is lifted to SalesPage so it survives tab switches. */
  filters: SalesFilters;
  onFiltersChange: (next: SalesFilters) => void;
  query: string;
  onQueryChange: (q: string) => void;
  /** Reports-style date range (presets + custom), lifted to SalesPage. */
  datePreset: DateRangePreset;
  customStart: string | null;
  customEnd: string | null;
  onDateChange: (
    preset: DateRangePreset,
    start: string | null,
    end: string | null,
  ) => void;
  /** The location's tag catalog for the Tags filter (hidden when empty). */
  tagOptions?: AppointmentTag[];
}

/** Human label for a payment type. CoF charges are type 'checkout' (the
 *  card-on-file distinction lives in the method, not the type). */
const PAYMENT_TYPE_LABELS: Record<string, string> = {
  deposit: "Deposit",
  checkout: "Checkout",
  refund: "Refund",
  other: "Other",
  package_redemption: "Package",
  loyalty_redemption: "Loyalty",
  gift_card_redemption: "Gift card",
};
const formatPaymentType = (t: string): string => PAYMENT_TYPE_LABELS[t] ?? t;

/**
 * Sales → Payments (one row per tender). Flattens each transaction's payments,
 * INCLUDING the loyalty / package redemption pseudo-tenders (shown as their own
 * rows, but kept out of the Captured/Net cash totals via `isCash`). Refund rows
 * are rendered defensively (override #5): from `refunds[]` if present, else from
 * `type === 'refund'` payments, else as a reduced note on the captured tender.
 */
function PurchasesSubTab({
  refreshNonce,
  filters,
  onFiltersChange,
  query,
  onQueryChange,
  datePreset,
  customStart,
  customEnd,
  onDateChange,
  tagOptions = [],
}: PurchasesSubTabProps) {
  const { businessInfo } = useSalon() as {
    businessInfo?: { id: string; salon_name?: string; timezone?: string };
  };
  // Render every datetime in the business location's timezone (not the
  // viewer's browser), falling back to the app default when the salon record
  // hasn't loaded a timezone yet.
  const timezone = businessInfo?.timezone || DEFAULT_TIMEZONE;
  const { printReceipt, receipt } = useReceiptPrint();
  const { salesLoading, salesError, fetchSales } = useTransaction();
  // Own rows (tender-grain) so the shared context state doesn't cross-
  // contaminate the Transactions tab (transaction-grain) and vice versa.
  const [rows, setRows] = useState<GetTransactionsResponse[]>([]);
  const { sendReceipt, getTransaction } = useApp();
  // Full-page-height drawer host owned by SalesPage; the drawer portals into it.
  const drawerSlot = useSalesDrawerSlot();

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [refundTarget, setRefundTarget] = useState<Payment | null>(null);
  const [voidTarget, setVoidTarget] = useState<GetTransactionsResponse | null>(
    null,
  );
  // Bumped after a void so the drawer's full-transaction fetch re-runs.
  const [drawerNonce, setDrawerNonce] = useState(0);
  const [emailing, setEmailing] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  // Appointment/transaction details opened in place from the drawer's ref link.
  // Kept in sync with the modal's updates (checkout, void) so it re-renders.
  const [apptTxn, setApptTxn] = useState<
    GetTransactionsResponse | TransactionResponse | null
  >(null);

  // Monotonic request id so a slow response can't overwrite the list after a
  // newer fetch (rapid date/filter switch, or refreshNonce firing mid-flight).
  const reqIdRef = useRef(0);
  const load = useCallback(async () => {
    if (!businessInfo?.id) return;
    // A half-set custom range shouldn't fire a query.
    if (!reportsRangeReady(datePreset, customStart, customEnd)) return;
    const [start, end] = resolveReportsRange(
      datePreset,
      customStart,
      customEnd,
    );
    const params = `start_date=${toIso(start)}&end_date=${toIso(end)}`;
    const myReqId = ++reqIdRef.current;
    const result = await fetchSales(businessInfo.id, "purchases", params);
    if (myReqId !== reqIdRef.current) return; // superseded by a newer fetch
    setRows(result ?? []);
  }, [businessInfo?.id, datePreset, customStart, customEnd, fetchSales]);

  useEffect(() => {
    load();
  }, [load, refreshNonce]);

  useEffect(() => {
    setPage(1);
  }, [query, filters, datePreset, customStart, customEnd]);

  // Flatten payments[] to tender rows. The backend is authoritative on refunds:
  // captured tenders come as their own rows, and each refund arrives as a
  // dedicated row carrying `refunds[]` (rendered via visibleRefunds). No
  // client-side refunded_amount annotation — that would double-count.
  const allTenders = useMemo<TenderRow[]>(() => {
    const out: TenderRow[] = [];
    rows.forEach((parent) => {
      listTenders(parent).forEach((p) => {
        out.push({
          key: `t-${p.id}`,
          parent,
          payment: p,
          method: formatPaymentMethod(p.method) || p.method,
          rawMethod: p.method,
          cardBrand: p.card_brand || undefined,
          last4: p.card_last4 || undefined,
          type: p.type,
          amount: p.amount,
          statusKey: "captured",
          time: p.created_dt,
          isRefund: false,
          isCash: isCashTender(p),
        });
      });

      visibleRefunds(parent).forEach((r) =>
        out.push({
          key: `r-${r.id}`,
          parent,
          method: formatPaymentMethod(r.method) || r.method,
          rawMethod: r.method,
          type: "refund",
          amount: -Math.abs(r.amount),
          statusKey: "refunded",
          time: r.created_dt,
          isRefund: true,
          isCash: false,
        }),
      );
    });
    return out;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^#/, "");
    const txnFilter = filters.txn.trim().toLowerCase().replace(/^#/, "");
    const clientFilter = filters.client.trim().toLowerCase();
    const last4Filter = filters.last4.trim();

    return allTenders
      .filter((r) =>
        filters.status === "all" ? true : r.statusKey === filters.status,
      )
      .filter((r) => {
        if (q.length === 0) return true;
        const ref = (r.parent.transaction?.reference_id || "").toLowerCase();
        return (
          ref.includes(q) || clientName(r.parent).toLowerCase().includes(q)
        );
      })
      .filter((r) =>
        txnFilter.length === 0
          ? true
          : (r.parent.transaction?.reference_id || "")
              .toLowerCase()
              .includes(txnFilter),
      )
      .filter((r) =>
        clientFilter.length === 0
          ? true
          : clientName(r.parent).toLowerCase().includes(clientFilter),
      )
      .filter((r) => {
        if (last4Filter.length === 0) return true;
        // The tender's own card_last4, or (for a transaction-wide match) any
        // payment on the parent transaction carrying that last4.
        return (
          r.last4 === last4Filter ||
          (r.parent.payments || []).some((p) => p.card_last4 === last4Filter)
        );
      })
      .filter((r) => matchesTagFilter(r.parent.tags, filters.tagIds)) // parent's tags
      .sort((a, b) => moment(b.time).diff(moment(a.time)));
  }, [allTenders, query, filters]);

  const summary = useMemo(() => {
    let captured = 0;
    let refunded = 0;
    // Tenders of a voided transaction stay listed but are out of every
    // report-style total.
    const counted = filteredRows.filter(
      (r) => r.parent.transaction?.transaction_status !== "void",
    );
    counted.forEach((r) => {
      if (r.isRefund) {
        refunded += Math.abs(r.amount);
        return;
      }
      // Loyalty / package redemptions are shown as rows but aren't cash.
      if (r.isCash) captured += r.amount;
    });
    return [
      { label: "Captured", value: formatCurrency(captured) },
      { label: "Payments", value: counted.length.toLocaleString() },
      {
        label: "Refunded",
        value: formatCurrency(refunded),
        danger: refunded > 0,
      },
      { label: "Net", value: formatCurrency(captured - refunded) },
    ];
  }, [filteredRows]);

  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  );

  const openRow = useMemo(
    () =>
      openKey ? (filteredRows.find((r) => r.key === openKey) ?? null) : null,
    [openKey, filteredRows],
  );

  // The Payments list carries only the clicked tender per row (and only tenders
  // within the date window), so fetch the full transaction to feed the shared
  // drawer every payment — the same complete data the Transactions tab already
  // has inline. One on-demand fetch per drawer open.
  const [fullTxn, setFullTxn] = useState<GetTransactionsResponse | null>(null);
  const openTxnId = openRow?.parent.transaction?.id ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!openTxnId) {
      setFullTxn(null);
      return;
    }
    (async () => {
      try {
        const resp = await getTransaction(openTxnId);
        if (!cancelled && resp) {
          setFullTxn(
            transformTransactionResponseToGetTransactionsResponse(resp),
          );
        }
      } catch {
        /* keep the partial list row on failure */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openTxnId, getTransaction, drawerNonce]);

  // Full transaction once loaded (all tenders); the partial list row until then.
  const drawerRow =
    fullTxn && fullTxn.transaction?.id === openTxnId
      ? fullTxn
      : (openRow?.parent ?? null);

  const stripePayment = useMemo(
    () => drawerRow?.payments.find((p) => !!p.stripe_payment_intent_id) ?? null,
    [drawerRow],
  );

  const sendEmailReceipt = useCallback(
    async (email: string) => {
      if (!stripePayment) return;
      setEmailing(true);
      try {
        await sendReceipt(stripePayment.id, email);
        notifySuccess(`The receipt was emailed to ${email}.`, {
          title: "Receipt sent",
        });
        setEmailModalOpen(false);
      } catch (err) {
        notifyError(err instanceof Error ? err.message : "Please try again.", {
          title: "Couldn't send receipt",
        });
      } finally {
        setEmailing(false);
      }
    },
    [stripePayment, sendReceipt],
  );

  return (
    <Box>
      {salesError && (
        <Text size="xs" c="red" mb="sm">
          Couldn&apos;t load purchases.{" "}
          <Anchor onClick={load} component="button" type="button" size="xs">
            Retry
          </Anchor>
        </Text>
      )}

      <Box>
        <Box>
          <SummaryStrip stats={summary} />

          <SalesToolbar
            query={query}
            onQueryChange={onQueryChange}
            filters={filters}
            onFiltersChange={onFiltersChange}
            defaultStatus={DEFAULT_STATUS}
            statusOptions={STATUS_OPTIONS}
            count={pagedRows.length}
            total={filteredRows.length}
            itemLabel="payments"
            searchPlaceholder="Search transaction #, client"
            loading={salesLoading}
            tagOptions={tagOptions}
            dateControl={
              <SalesDateRangePill
                preset={datePreset}
                customStart={customStart}
                customEnd={customEnd}
                onChange={onDateChange}
              />
            }
          />

          <SalesTable
            columnTemplate={COLUMN_TEMPLATE}
            minWidth={MIN_WIDTH}
            headers={[
              { label: "Transaction Id" },
              { label: "Date" },
              { label: "Method" },
              { label: "Type" },
              { label: "Client" },
              { label: "Amount", align: "right" },
              { label: "" },
            ]}
            loading={salesLoading}
            isEmpty={filteredRows.length === 0}
            emptyText="No payments match these filters."
          >
            {pagedRows.map((r) => (
              <SalesTableRow
                key={r.key}
                active={openKey === r.key}
                onClick={() =>
                  setOpenKey((cur) => (cur === r.key ? null : r.key))
                }
              >
                <Box className={styles.cellNum}>
                  {r.parent.transaction?.reference_id || "—"}
                </Box>
                <Box className={styles.cellNum}>
                  {formatDateTime(r.time, timezone, "MMM D · h:mm A")}
                </Box>
                <Group gap={7} wrap="nowrap" className={styles.cellEllipsis}>
                  <PaymentMethodIcon
                    method={r.rawMethod}
                    brand={r.cardBrand}
                    isRefund={r.isRefund}
                  />
                  <Box className={styles.cellEllipsis}>
                    {methodLabel(r.rawMethod, r.method, r.cardBrand, r.last4)}
                  </Box>
                </Group>
                <Box>{formatPaymentType(r.type)}</Box>
                <Box className={styles.cellEllipsis}>
                  {clientName(r.parent)}
                </Box>
                <Box
                  className={`${styles.cellRight} ${styles.cellNum} ${
                    r.amount < 0 ? styles.paymentAmountNeg : ""
                  }`}
                >
                  {formatCurrency(r.amount)}
                </Box>
              </SalesTableRow>
            ))}
          </SalesTable>

          <SalesPagination
            page={page}
            pageSize={pageSize}
            total={filteredRows.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
            itemLabel="payments"
          />
        </Box>

        {openRow &&
          drawerSlot &&
          createPortal(
            <TransactionDetailDrawer
              key={openTxnId ?? "none"}
              overlay={false}
              row={drawerRow ?? openRow.parent}
              timezone={timezone}
              onClose={() => setOpenKey(null)}
              onRefund={setRefundTarget}
              onPrint={() => printReceipt(drawerRow ?? openRow.parent)}
              onEmail={() => setEmailModalOpen(true)}
              emailDisabled={!stripePayment}
              emailLoading={emailing}
              onVoid={() => setVoidTarget(drawerRow ?? openRow.parent)}
              onOpenAppointment={
                openRow.parent.transaction?.id
                  ? () => setApptTxn(openRow.parent)
                  : undefined
              }
            />,
            drawerSlot,
          )}
      </Box>

      {refundTarget && (
        <RefundPaymentModal
          opened
          payment={refundTarget}
          onClose={() => setRefundTarget(null)}
          onSuccess={load}
        />
      )}

      {voidTarget?.transaction?.id && businessInfo?.id && (
        <VoidTransactionModal
          opened
          businessLocationId={businessInfo.id}
          transactionId={voidTarget.transaction.id}
          referenceId={voidTarget.transaction.reference_id}
          onClose={() => setVoidTarget(null)}
          onSuccess={() => {
            setDrawerNonce((n) => n + 1);
            return load();
          }}
        />
      )}

      {apptTxn && (
        <TransactionDetailModal
          opened
          transaction={apptTxn}
          onClose={() => setApptTxn(null)}
          onTransactionUpdated={(updated) => {
            setApptTxn(updated);
            setDrawerNonce((n) => n + 1);
            load();
          }}
        />
      )}

      {receipt}

      <EmailReceiptModal
        opened={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        clientEmail={openRow?.parent.client?.email || undefined}
        onSend={sendEmailReceipt}
        loading={emailing}
      />
    </Box>
  );
}

export default PurchasesSubTab;
